import {
  SEED_MARKETS,
  marketsForScope,
  type Click,
  type Market,
  type Merchant,
  type MerchantRegion,
} from '../dataProvider/types';
import { MATURATION_DAYS, CLICK_FLOOR } from '../reports/maturation';

/**
 * The Exit clicks query/aggregation module - the ONE place outbound leakage is
 * computed. Same seam idiom as storePerformanceModel.ts: v1 aggregates
 * client-side from the mock provider, every selector lives here, and the late
 * real-data migration swaps this module for a server-side endpoint without the
 * surface changing. The surface never sees a raw click.
 *
 * It is the destination-side mirror of Store performance. Store performance asks
 * "how well does each store convert its own clicks"; Exit clicks asks "where is
 * our outbound traffic being wasted". It leads on LEAKAGE: the destinations
 * soaking up exits and returning nothing.
 *
 * THE LEAK (the core mechanic). A leak is a MATURED non-converting exit: a click
 * is a leak only if its occurredAt is at least MATURATION_DAYS old AND
 * converted === false. Fresh clicks (younger than MATURATION_DAYS) have not had
 * time to convert, so they are NOT yet judged and are excluded from the leak
 * count entirely. This is the same maturation cutoff as Store performance, the
 * same occurredAt field and the same shared MATURATION_DAYS, applied to the
 * NUMERATOR (the leak count) rather than a rate denominator. A naive count of
 * converted === false across the window is WRONG: it would brand every store with
 * recent traffic a leak. That trap is the whole reason this surface exists.
 *
 * CONVERSION SOURCE OF TRUTH is the click's `converted` boolean, the same as
 * Store performance (D-store-performance). The aggregator reads only clicks,
 * merchants and merchant regions (never transactions), so the
 * transactions.clickId join cannot leak in.
 */

// --- tunables (flagged for review) -----------------------------------------

// MATURATION_DAYS and CLICK_FLOOR are SHARED with Store performance and defined
// once in src/reports/maturation.ts; re-exported here for callers that read them
// from this model. Same cutoff, same floor, both funnel surfaces move together.
export { MATURATION_DAYS, CLICK_FLOOR } from '../reports/maturation';

// --- types ------------------------------------------------------------------

/** The period control. Opens on last 30 days rolling (D-exit-clicks). */
export type ExitClicksPeriod = 'last30' | 'mtd' | 'lastMonth';

export const PERIOD_OPTIONS: ReadonlyArray<{
  id: ExitClicksPeriod;
  label: string;
}> = [
  { id: 'last30', label: 'Last 30 days' },
  { id: 'mtd', label: 'Month to date' },
  { id: 'lastMonth', label: 'Last full calendar month' },
];

export interface ExitClicksRow {
  /** Stable key, e.g. `region:10`. */
  key: string;
  /** The parent merchant's display name. */
  name: string;
  /** Total exits in the selected period (full period, matured or not). */
  totalExits: number;
  /** Matured clicks in the period (the leak-rate denominator). */
  maturedClicks: number;
  /** Matured non-converting exits: the absolute leak magnitude. */
  leakVolume: number;
  /** leakVolume / maturedClicks, or null when no matured clicks. */
  leakRate: number | null;
  /** True when maturedClicks is below the floor (kept out of the ranked set). */
  belowFloor: boolean;
}

export interface ExitClicksBand {
  market: Market;
  /** Stores at or above the click floor, sorted by leak rate DESCENDING. */
  ranked: ExitClicksRow[];
  /** Stores below the floor (includes fresh, no-rate stores), unranked. */
  subFloor: ExitClicksRow[];
  /** Band norm: the median leak rate over ranked stores, or null. */
  medianRate: number | null;
}

export interface ExitClicksView {
  bands: ExitClicksBand[];
}

export interface ExitClicksInput {
  clicks: Click[];
  merchants: Merchant[];
  merchantRegions: MerchantRegion[];
}

export interface ExitClicksOptions {
  /** Access scope (UK | US | GLOBAL); decides which market bands render. */
  scope: string;
  period: ExitClicksPeriod;
  /** Reference clock (epoch ms) for the period window and the maturation cutoff. */
  now: number;
}

// --- helpers ----------------------------------------------------------------

const DAY = 86_400_000;

/** The market bands a scope renders: scope -> markets, kept to operating markets. */
export const bandsForScope = (scope: string): Market[] =>
  marketsForScope(scope).filter((m) => SEED_MARKETS.includes(m));

/** Inclusive [start, end] ms window for the selected period (same as the others). */
function periodWindow(
  period: ExitClicksPeriod,
  now: number,
): { start: number; end: number } {
  const d = new Date(now);
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();
  switch (period) {
    case 'last30':
      return { start: now - 30 * DAY, end: now };
    case 'mtd':
      return { start: Date.UTC(y, m, 1), end: now };
    case 'lastMonth':
      return { start: Date.UTC(y, m - 1, 1), end: Date.UTC(y, m, 1) - 1 };
  }
}

const inWindow = (ts: number, w: { start: number; end: number }): boolean =>
  ts >= w.start && ts <= w.end;

const median = (values: number[]): number | null => {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
};

interface Accum {
  name: string;
  totalExits: number;
  maturedClicks: number;
  leakVolume: number;
}

// --- aggregation ------------------------------------------------------------

/** Build the full view for the active scope (one band per derived market). */
export function buildExitClicksView(
  input: ExitClicksInput,
  opts: ExitClicksOptions,
): ExitClicksView {
  const bands = bandsForScope(opts.scope).map((market) =>
    buildBand(market, input, opts),
  );
  return { bands };
}

function buildBand(
  market: Market,
  input: ExitClicksInput,
  opts: ExitClicksOptions,
): ExitClicksBand {
  const regionById = new Map(input.merchantRegions.map((r) => [r.id, r]));
  const merchantById = new Map(input.merchants.map((m) => [m.id, m]));

  const window = periodWindow(opts.period, opts.now);
  // A click is matured when its occurredAt is at least MATURATION_DAYS old,
  // measured from `now` (not from the window edge).
  const maturityCutoff = opts.now - MATURATION_DAYS * DAY;

  // Clicks for this market that fall in the selected period.
  const clicks = input.clicks.filter(
    (c) => c.market === market && inWindow(Date.parse(c.occurredAt), window),
  );

  // Group by merchantRegion (the per-market store).
  const accums = new Map<number, Accum>();
  for (const c of clicks) {
    const regionId = c.merchantRegionId;
    const region = regionById.get(regionId);
    const merchantId = region?.merchantId ?? c.merchantId;
    const name =
      merchantById.get(merchantId)?.name ?? `Store ${String(regionId)}`;
    const acc = accums.get(regionId) ?? {
      name,
      totalExits: 0,
      maturedClicks: 0,
      leakVolume: 0,
    };
    acc.totalExits += 1;
    const matured = Date.parse(c.occurredAt) <= maturityCutoff;
    if (matured) {
      acc.maturedClicks += 1;
      // A leak is a MATURED non-converting exit. The conversion signal is the
      // click's `converted` boolean, never the transactions.clickId join. Fresh
      // clicks never reach here, so recency is never counted as leakage.
      if (!c.converted) acc.leakVolume += 1;
    }
    accums.set(regionId, acc);
  }

  const rows: ExitClicksRow[] = [...accums.entries()].map(([regionId, a]) => ({
    key: `region:${String(regionId)}`,
    name: a.name,
    totalExits: a.totalExits,
    maturedClicks: a.maturedClicks,
    leakVolume: a.leakVolume,
    leakRate: a.maturedClicks > 0 ? a.leakVolume / a.maturedClicks : null,
    belowFloor: a.maturedClicks < CLICK_FLOOR,
  }));

  const ranked = rows
    .filter((r) => !r.belowFloor)
    // Default: leak rate descending, so the worst-wasting destinations sit on
    // top. Ranked rows always have a rate (maturedClicks >= the floor).
    .sort((a, b) => (b.leakRate ?? 0) - (a.leakRate ?? 0));
  const subFloor = rows
    .filter((r) => r.belowFloor)
    .sort((a, b) => a.name.localeCompare(b.name));

  const medianRate = median(
    ranked.map((r) => r.leakRate).filter((v): v is number => v != null),
  );

  return { market, ranked, subFloor, medianRate };
}
